import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { Input } from './ui/input';
import { GET_APP_CONFIG } from '../graphql/queries';

// Use any for Google Maps types to avoid conflicts with other declarations
interface GoogleAutocompleteService {
  getPlacePredictions(
    request: { input: string; types?: string[]; componentRestrictions?: { country: string } },
    callback: (results: GooglePrediction[] | null, status: string) => void
  ): void;
}

interface GoogleGeocoder {
  geocode(
    request: { placeId: string },
    callback: (results: GoogleGeocoderResult[] | null, status: string) => void
  ): void;
}

interface GooglePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface GoogleGeocoderResult {
  address_components: GoogleAddressComponent[];
  formatted_address: string;
}

interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface ParsedAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  placeholder?: string;
  className?: string;
}

// Load Google Maps script dynamically
const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const google = (window as any).google;
    if (google?.maps?.places) {
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      return;
    }

    if (!apiKey) {
      reject(new Error('Google Places API key not configured'));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
};

// Parse Google address components into our format
const parseAddressComponents = (components: GoogleAddressComponent[]): ParsedAddress => {
  let streetNumber = '';
  let route = '';
  let city = '';
  let state = '';
  let zipCode = '';

  for (const component of components) {
    const types = component.types;

    if (types.includes('street_number')) {
      streetNumber = component.long_name;
    } else if (types.includes('route')) {
      route = component.long_name;
    } else if (types.includes('locality') || types.includes('sublocality_level_1')) {
      city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      state = component.short_name; // Use short name for state (e.g., "CA" instead of "California")
    } else if (types.includes('postal_code')) {
      zipCode = component.long_name;
    }
  }

  const street1 = streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber;

  return {
    street1,
    street2: '',
    city,
    state,
    zipCode,
    fullAddress: `${street1}, ${city}, ${state} ${zipCode}`.trim(),
  };
};

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Start typing your address...',
  className = '',
}: AddressAutocompleteProps) {
  const [predictions, setPredictions] = useState<GooglePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const placesServiceRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<GoogleAutocompleteService | null>(null);
  const geocoder = useRef<GoogleGeocoder | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch API key from backend
  const { data: configData, loading: configLoading } = useQuery<{ getAppConfig: { googlePlacesApiKey: string } }>(GET_APP_CONFIG, {
    fetchPolicy: 'cache-first',
  });

  // Load Google Maps and initialize services
  useEffect(() => {
    const apiKey = configData?.getAppConfig?.googlePlacesApiKey;
    if (!apiKey) return;

    loadGoogleMapsScript(apiKey)
      .then(() => {
        const google = (window as any).google;
        if (google) {
          autocompleteService.current = new google.maps.places.AutocompleteService();
          geocoder.current = new google.maps.Geocoder();
          setIsGoogleLoaded(true);
        }
      })
      .catch((err) => {
        console.warn('Google Places not available:', err.message);
      });
  }, [configData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteService.current || input.length < 3) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    autocompleteService.current.getPlacePredictions(
      {
        input,
        types: ['address'],
        componentRestrictions: { country: 'us' },
      },
      (results: GooglePrediction[] | null, status: string) => {
        setIsLoading(false);
        if (status === 'OK' && results) {
          setPredictions(results);
          setIsOpen(true);
        } else {
          setPredictions([]);
        }
      }
    );
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (!isGoogleLoaded) return;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 300);
  };

  const handleSelectPrediction = async (prediction: GooglePrediction) => {
    onChange(prediction.description);
    setPredictions([]);
    setIsOpen(false);

    // Use Geocoder to get address components
    if (geocoder.current) {
      geocoder.current.geocode(
        { placeId: prediction.place_id },
        (results: GoogleGeocoderResult[] | null, status: string) => {
          if (status === 'OK' && results && results[0]) {
            const parsed = parseAddressComponents(results[0].address_components);
            parsed.fullAddress = prediction.description;
            onAddressSelect(parsed);
          } else {
            // Fallback: parse address from description if geocoding fails
            const parts = prediction.description.split(',').map(p => p.trim());
            const fallbackAddress: ParsedAddress = {
              street1: parts[0] || '',
              street2: '',
              city: parts[1] || '',
              state: parts[2]?.split(' ')[0] || '',
              zipCode: parts[2]?.split(' ')[1] || '',
              fullAddress: prediction.description,
            };
            onAddressSelect(fallbackAddress);
          }
        }
      );
    }
  };

  if (configLoading) {
    return (
      <div className={`relative ${className}`}>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Loading address search..."
          disabled
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Hidden div for PlacesService */}
      <div ref={placesServiceRef} style={{ display: 'none' }} />
      
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <Input
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            if (predictions.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          className="pl-10"
        />
      </div>
      
      {isOpen && (predictions.length > 0 || isLoading) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
              <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Searching addresses...
            </div>
          ) : (
            predictions.map((prediction) => (
              <button
                key={prediction.place_id}
                type="button"
                onClick={() => handleSelectPrediction(prediction)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-aalaw-red mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {prediction.structured_formatting.main_text}
                    </p>
                    <p className="text-xs text-gray-500">
                      {prediction.structured_formatting.secondary_text}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              </svg>
              Powered by Google
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
